import {
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { removeTokenCookies, removeTokens } from "@saleor/auth";
import { isSuperUser } from "@saleor/auth/misc";
import { tokenVerifyMutation } from "@saleor/auth/mutations";
import {
  VerifyToken,
  VerifyTokenVariables
} from "@saleor/auth/types/VerifyToken";
import { getPermissionDefaultPath } from "@saleor/auth/urls";
import {
  Account,
  useSavedAccount
} from "@saleor/components/AppLayout/SavedAccountContext";
import { useSwitchAccount } from "@saleor/components/AppLayout/SwitchAccountContext";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import classNames from "classnames";
import Cookies from "js-cookie";
import React from "react";
import { useApolloClient, useMutation } from "react-apollo";

import useStyles from "./styles";

interface SavedAccountListProps {
  accountOptions: Account[];
  handleLogout: () => void;
  handleRemoveAccount: (email: string) => void;
  setMenuState: (state: boolean) => void;
}

const SavedAccountsList: React.FC<SavedAccountListProps> = ({
  accountOptions,
  handleLogout,
  handleRemoveAccount,
  setMenuState
}) => {
  const classes = useStyles({});
  const apolloClient = useApolloClient();
  const notify = useNotifier();
  const navigate = useNavigator();
  const { loginByToken } = useUser();
  const { handleUpdateSavedAccount } = useSavedAccount();
  const [tokenVerify] = useMutation<VerifyToken, VerifyTokenVariables>(
    tokenVerifyMutation,
    {
      client: apolloClient,
      onCompleted: result => {
        if (result.tokenVerify === null) {
          handleLogout();
        }
      },
      onError: handleLogout
    }
  );

  const { switchingAccount, setSwitchingAccount } = useSwitchAccount();

  const handleSwitchAccount = ({ token, csrf, email }: Account) => {
    if (switchingAccount) {
      return;
    }

    setSwitchingAccount(true);
    const isSwitching = true;
    tokenVerify({
      variables: {
        isSwitching,
        token
      }
    })
      .then(result => {
        if (
          result === undefined ||
          result?.data?.tokenVerify?.user === undefined
        ) {
          failedLoginResponse();
        } else {
          if (result.data.tokenVerify.user.isMfaEnabled) {
            removeTokenCookies();
            removeTokens();
            const { user } = result.data.tokenVerify!;

            const mfaLocalStorage = {
              defaultMfaMethod: user.defaultMfaMethod,
              mfaRemainingGraceDays: user.mfaRemainingGraceDays,
              userContactNo: user.contactNo,
              userEmail: user.email
            };

            localStorage.setItem(
              "mfaLocalStorage",
              JSON.stringify(mfaLocalStorage)
            );
            Cookies.set("mfaContactNo", user.contactNo);
            Cookies.set("mfaSetupDate", user.mfaSetupDate);
            Cookies.remove("hasMaxAttemptsEmail");
            Cookies.remove("hasMaxAttemptsPhone");

            if (user.mfaSetupDate) {
              const cooldownSeconds =
                result.data.tokenVerify.data.resendCooldown || 300;
              Cookies.set(
                "mfaCreatedAt",
                result.data.tokenVerify.data.createdAt,
                {
                  expires: cooldownSeconds / 86400
                }
              );
              Cookies.set("mfaResendCooldown", cooldownSeconds.toString(), {
                expires: cooldownSeconds / 86400
              });

              const mfaState = {
                createdAt: result.data.tokenVerify.data.createdAt,
                email: user.email,
                isEmailSendCode:
                  user.defaultMfaMethod === "EMAIL" ? true : false
              };
              if (user.defaultMfaMethod === "EMAIL") {
                localStorage.setItem("mfaState", JSON.stringify(mfaState));

                if (
                  result.data.tokenVerify.data.mfaAttempts >= 3 ||
                  result.data.tokenVerify.data.lockedAt
                ) {
                  Cookies.set("EmailLockedOut", "true");
                }
                window.location.href = "/mfa-email-input-page/";
              } else {
                mfaState.isEmailSendCode =
                  user.defaultMfaMethod === "PHONE" ? true : false;
                localStorage.setItem("mfaState", JSON.stringify(mfaState));
                if (
                  result.data.tokenVerify.data.mfaAttempts >= 3 ||
                  result.data.tokenVerify.data.lockedAt
                ) {
                  Cookies.set("PhoneLockedOut", "true");
                }
                window.location.href = "/mfa-phone-input-page/";
              }
            } else {
              Cookies.remove("PhoneLockedOut");
              Cookies.remove("EmailLockedOut");
              Cookies.remove("lockoutUntil");
              window.location.href = "/mfa-verification-page/";
            }
            return;
          } else {
            loginByToken(token, csrf, result.data.tokenVerify.user);
            handleUpdateSavedAccount(email, {
              lastLogin: Date.now()
            });
            const defaultPermissionPath = getPermissionDefaultPath(
              result.data.tokenVerify.user
            );

            if (
              defaultPermissionPath &&
              !isSuperUser(result.data.tokenVerify.user)
            ) {
              navigate(defaultPermissionPath.path);
              return;
            }
            navigate("/");
          }
        }
      })
      .catch(() => {
        failedLoginResponse();
      })
      .finally(() => {
        setMenuState(false);
        setSwitchingAccount(false);
      });
  };

  const failedLoginResponse = () => {
    notify({
      status: "error",
      text: "Failed to log in saved account. Token expired."
    });
  };

  return (
    <>
      <Typography component="div" className={classes.switchAccountLabel}>
        SWITCH ACCOUNT
      </Typography>
      <List disablePadding className={classes.accountList}>
        {accountOptions.map((savedAccount, index) => (
          <SavedAccount
            savedAccount={savedAccount}
            key={index}
            handleRemoveAccount={handleRemoveAccount}
            handleSwitchAccount={handleSwitchAccount}
          />
        ))}
      </List>
    </>
  );
};

interface SavedAccountProps {
  savedAccount: Account;
  handleRemoveAccount: (email: string) => void;
  handleSwitchAccount: (savedAccount: Account) => void;
}

const SavedAccount: React.FC<SavedAccountProps> = ({
  savedAccount,
  handleRemoveAccount,
  handleSwitchAccount
}) => {
  const classes = useStyles({});
  const [showTrashButton, setShowTrashButton] = React.useState(false);

  return (
    <ListItem
      disableGutters
      className={classes.accountListItem}
      onMouseEnter={() => setShowTrashButton(true)}
      onMouseLeave={() => setShowTrashButton(false)}
    >
      <ListItemText
        className={classes.accountLabels}
        onClick={() => handleSwitchAccount(savedAccount)}
      >
        <Typography component="div" className={classes.nameText}>
          {savedAccount.firstName} {savedAccount.lastName}
        </Typography>
        <Typography component="div" className={classes.emailText}>
          {savedAccount.email}
        </Typography>
      </ListItemText>
      <ListItemIcon
        className={classNames(classes.trashIcon, {
          [classes.hiddenTrashButton]: !showTrashButton
        })}
      >
        <IconButton
          className={classes.trashButton}
          onClick={() => handleRemoveAccount(savedAccount.email)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </ListItemIcon>
    </ListItem>
  );
};

SavedAccountsList.displayName = "SavedAccountsList";
export default SavedAccountsList;
